import { DailyActivity } from '../types';

/** Legacy top-level Workout or Run with Workout subtype — uses split miles, not milesRun. */
export function isWorkoutSplitMilesActivity(
  activity: Pick<DailyActivity, 'runType' | 'runSubType'>
): boolean {
  if (!activity?.runType) return false;
  if (activity.runType === 'Workout') return true;
  return activity.runType === 'Run' && activity.runSubType === 'Workout';
}

/** Run + Race Day: race distance is for results / display only, not training volume. */
export function isRunRaceDayActivity(
  activity: Pick<DailyActivity, 'runType' | 'runSubType'>
): boolean {
  return activity.runType === 'Run' && activity.runSubType === 'Race Day';
}

/**
 * Miles that count toward volume (totals, charts, challenges, team reports).
 * - Run + Race Day: 0 (log race distance under workouts for mileage)
 * - Run (non-Workout subtype, non–Race Day): milesRun
 * - Workout (legacy) or Run + Workout subtype: warmup + workout + cooldown
 * - Others: 0
 */
export function getActivityMiles(activity: Pick<DailyActivity, 'runType' | 'runSubType' | 'milesRun' | 'warmupMiles' | 'workoutMiles' | 'cooldownMiles'>): number {
  if (!activity?.runType) return 0;
  if (isWorkoutSplitMilesActivity(activity)) {
    return (activity.warmupMiles ?? 0) + (activity.workoutMiles ?? 0) + (activity.cooldownMiles ?? 0);
  }
  if (activity.runType === 'Run') {
    if (isRunRaceDayActivity(activity)) return 0;
    return activity.milesRun ?? 0;
  }
  return 0;
}

/**
 * Miles to show on a single activity row (includes Race Day distance; excludes nothing).
 */
export function getActivityMilesForDisplay(
  activity: Pick<DailyActivity, 'runType' | 'runSubType' | 'milesRun' | 'warmupMiles' | 'workoutMiles' | 'cooldownMiles'>
): number {
  if (isRunRaceDayActivity(activity)) {
    return activity.milesRun ?? 0;
  }
  return getActivityMiles(activity);
}

/**
 * Check if an activity has logged miles or cross-training / strength minutes.
 * Used for determining if a user has completed running activity for the day.
 */
export function hasMilesOrMinutes(
  activity: Pick<
    DailyActivity,
    | 'runType'
    | 'runSubType'
    | 'milesRun'
    | 'warmupMiles'
    | 'workoutMiles'
    | 'cooldownMiles'
    | 'crossTrainingMinutes'
    | 'strengthMinutes'
  >
): boolean {
  if (!activity?.runType || activity.runType === 'None') return false;
  if (['Rest Day', 'Sick', 'Injured'].includes(activity.runType)) return true;
  if (isWorkoutSplitMilesActivity(activity)) {
    return getActivityMiles(activity) > 0;
  }
  if (activity.runType === 'Run') {
    return (activity.milesRun ?? 0) > 0;
  }
  if (activity.runType === 'Cross Training') {
    return (activity.crossTrainingMinutes ?? 0) > 0;
  }
  if (activity.runType === 'Strength') {
    return (activity.strengthMinutes ?? 0) > 0;
  }
  return false;
}
